// dashboard/utils.js
// Pure data-transform helpers, extracted from App.js (2026-08-12 split) —
// no JSX, no component state. See dashboard/constants.js for colors/labels.

import i18n from '../i18n';
import { supabase } from '../supabase';
import { REASON_CODE_CLINIC_LABEL } from './constants';

// Weight is always stored in kg. Convert for display based on viewer preference.
export const KG_TO_LBS = 2.20462;
export function fmtWeight(kg, useLbs) {
  if (useLbs) return `${(parseFloat(kg) * KG_TO_LBS).toFixed(1)} lbs`;
  return `${parseFloat(kg).toFixed(1)} kg`;
}
export function toDisplayWeight(kg, useLbs) {
  return useLbs ? parseFloat(kg) * KG_TO_LBS : parseFloat(kg);
}

// Release-blocker #1 (docs/BUILD_STATE.md, 2026-09-27, Bryan): no raw
// numeric score anywhere client-facing. Both StatusNarrative.js and
// PatientDetailPanel.js used to interpolate clinical_score/
// clinical_score_delta directly into text ("Score 57 (+11)") — this
// derives a qualitative word from the delta's SIGN only, never its
// magnitude or the score itself, shared so the two surfaces can't drift.
export function trendDirectionLabel(delta, t) {
  if (delta == null) return t('patientCard.directionUnknown');
  if (Math.abs(delta) < 1) return t('patientCard.directionStable');
  return delta > 0 ? t('patientCard.directionImproving') : t('patientCard.directionDeclining');
}

export function relDate(iso) {
  if (!iso) return '—';
  const days = Math.round((new Date() - new Date(iso)) / 86400000);
  if (days === 0) return i18n.t('dates.today');
  if (days === 1) return i18n.t('dates.yesterday');
  if (days < 7)  return i18n.t('dates.daysAgo', { count: days });
  if (days < 30) return i18n.t('dates.weeksAgo', { count: Math.round(days/7) });
  // Locale-aware, not just string-aware — this previously hardcoded
  // 'en-US' so dates wouldn't follow the app's language.
  return new Date(iso).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
}

export function getAge(dob) {
  if (!dob) return '';
  return `${Math.floor((new Date() - new Date(dob)) / (365.25*86400000))}y`;
}

// eslint-disable-next-line no-unused-vars
export function timeOfDay() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length !== 10) return raw || '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Groups per-condition patient rows by pet. Each group gets the highest
// severity across all its conditions. Conditions within a group are sorted
// highest-severity-first so the most critical appears at the top of the card.
//
// Pet-group order follows the *first appearance* of each pet in `rows` —
// i.e. whatever order the caller already sorted rows into (App.js's
// `filtered` useMemo sorts by filters.sortBy: severity/lastUpdated/name)
// — via Object.values' insertion-order guarantee for string keys. This
// function previously re-sorted the final groups alphabetically
// regardless of input order, silently discarding the caller's sort and
// making the dashboard's "Sort by" control a no-op no matter what a
// clinician picked (Phase 8 audit, 2026-07-15).
export function groupPatients(rows) {
  const SEV = { immediate_review: 0, red: 1, yellow: 2, green: 3 };
  const byPet = {};
  for (const row of rows) {
    const pid = row.pet?.id;
    if (!pid) continue;
    if (!byPet[pid]) byPet[pid] = { pet: row.pet, owner: row.owner, conditions: [], severity: 'green' };
    byPet[pid].conditions.push(row);
    if (SEV[row.severity] < SEV[byPet[pid].severity]) byPet[pid].severity = row.severity;
  }
  for (const pg of Object.values(byPet)) {
    pg.conditions.sort((a, b) => SEV[a.severity] - SEV[b.severity]);
  }
  return Object.values(byPet);
}

// Lazy-fetches 3-month tracker data per pet when a card's tracker section
// opens. freezeCutoff (past-due grace period elapsed) hides entries dated
// after the freeze instant — this preview previously had no such filter at
// all, unlike PatientDetailPanel's equivalent tracker fetches and the PDF
// export, so a lapsed clinic's roster still showed live, unfiltered trend
// data in this specific spot even though the same data was correctly
// frozen everywhere else.
export async function fetchTrackerData(petId, freezeCutoff) {
  const since3m  = new Date(); since3m.setMonth(since3m.getMonth() - 3);
  const since6m  = new Date(); since6m.setMonth(since6m.getMonth() - 6);
  const since12m = new Date(); since12m.setFullYear(since12m.getFullYear() - 1);
  const sinceDate3m = since3m.toISOString().split('T')[0];
  let weightsQ = supabase.from('weight_entries').select('weight,unit,measured_at')
    .eq('pet_id', petId).gte('measured_at', since12m.toISOString().split('T')[0])
    .is('deleted_at', null).order('measured_at', { ascending: false }).limit(6);
  let goodDaysQ = supabase.from('good_day_logs').select('log_date,value')
    .eq('pet_id', petId).gte('log_date', sinceDate3m).is('deleted_at', null).order('log_date', { ascending: false });
  let seizuresQ = supabase.from('seizure_logs').select('occurred_at,duration_bucket')
    .eq('pet_id', petId).gte('occurred_at', since6m.toISOString())
    .is('deleted_at', null).order('occurred_at', { ascending: false }).limit(10);
  if (freezeCutoff) {
    const cutoffISO = freezeCutoff.toISOString();
    weightsQ  = weightsQ.lte('measured_at', cutoffISO);
    goodDaysQ = goodDaysQ.lte('log_date', cutoffISO);
    seizuresQ = seizuresQ.lte('occurred_at', cutoffISO);
  }
  const [wRes, gRes, sRes] = await Promise.all([weightsQ, goodDaysQ, seizuresQ]);
  return { weights: wRes.data || [], goodDays: gRes.data || [], seizures: sRes.data || [] };
}

// Same reasoning as SetPasswordScreen's own withTimeout — a stalled
// Supabase call (seen on a first load right after auth) should surface as
// a failure after a bounded wait instead of leaving a loading state stuck
// forever with no way out short of a page reload. Generic (no i18n) since
// callers so far only use it for non-fatal, console-logged fetches, not a
// message shown verbatim to the user.
export function withTimeout(promise, ms = 10000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
  ]);
}

export function calcPrintAge(dob) {
  if (!dob) return '';
  const d = new Date(dob + 'T00:00:00');
  const now = new Date();
  const years = Math.floor((now - d) / (365.25 * 86400000));
  const months = Math.floor(((now - d) % (365.25 * 86400000)) / (30.44 * 86400000));
  if (years === 0) return i18n.t('printSummary.ageMonths', { count: months });
  return months > 0
    ? `${i18n.t('printSummary.ageYears', { count: years })}, ${i18n.t('printSummary.ageMonths', { count: months })}`
    : i18n.t('printSummary.ageYears', { count: years });
}

// Plain <title> tooltip (native browser hover, no extra component/JS
// needed) — survey_evaluations' severity_band/change_state/reason_code/
// sentinel findings had no UI representation anywhere before this.
//
// Release-blocker #1/#2 (docs/BUILD_STATE.md, 2026-09-27, Bryan): no raw
// score digits ("score 57"), and prefer the vNext plain-English findings
// narrative (vNextFindingsLines, from buildFindingsNarrative — the
// caller's job to compute, this module stays free of clinicalScoring
// knowledge) over the OLD engine's raw finding CODES (survey_
// evaluations.reasons[0].findings, e.g. "anemia_compatible" — exactly the
// "not a why story" gap this was built to fix) whenever vNext has
// evaluated this submission.
export function formatEvaluationTooltip(dateStr, trendWord, evaluation, vNextFindingsLines) {
  const lines = [trendWord ? `${dateStr} — ${trendWord}` : dateStr];
  if (Array.isArray(vNextFindingsLines) && vNextFindingsLines.length > 0) {
    lines.push(...vNextFindingsLines);
    return lines.join('\n');
  }
  if (evaluation) {
    const bandLine = [evaluation.severity_band, evaluation.change_state].filter(Boolean).join(' / ');
    if (bandLine) lines.push(bandLine);
    const reasonLabel = REASON_CODE_CLINIC_LABEL[evaluation.primary_reason_code] || evaluation.primary_reason_code;
    if (reasonLabel) lines.push(reasonLabel);
  }
  return lines.join('\n');
}
